/**
 * 마이 — 프로필 + 설정 메뉴 (라인 구분 리스트)
 * 결제/정산 내역은 역할(원장/학부모)에 따라 라벨이 달라짐
 * 프로필 사진: 아바타 탭 → 이미지 업로드 (Storage avatars/{uid})
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronForward, IoCameraOutline } from 'react-icons/io5';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';
import { doc, updateDoc, getDocs, query, collection, where, limit } from 'firebase/firestore';
import { db, storage, auth } from '../../firebase/config';
import { APP_CONFIG } from '../../config/appConfig';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { useDialog } from '../../context/DialogContext';
import ModeSwitchSplash from '../../components/common/ModeSwitchSplash';
import ChildrenSheet from '../../components/parent/ChildrenSheet';
import ChildAvatar from '../../components/parent/ChildAvatar';

const APP_VERSION = 'v1.0.0';
const currentYear = new Date().getFullYear();
const koreanAge = (year) => (year ? currentYear - year + 1 : null);

const MyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, logout, refreshUser } = useAuth();
  const { operatedAcademies, operatedAcademy, mode, setMode } = useAcademy();
  const { confirm, alert, showLoading, hideLoading } = useDialog();

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [switching, setSwitching] = useState(null);
  const [nameModal, setNameModal] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameErr, setNameErr] = useState('');
  const [childSheetOpen, setChildSheetOpen] = useState(false);

  useEffect(() => {
    if (location.state?.openChild) setChildSheetOpen(true);
  }, [location.state]);

  const openChildSheet = () => setChildSheetOpen(true);

  const isOwner = (operatedAcademies || []).length > 0;
  const academyMode = mode === 'academy';
  const billingLabel = isOwner ? '정산 내역' : '결제 내역';
  const isDemo = userData?.provider === 'demo';

  const soon = (name) => alert(`${name} 기능은 준비 중이에요.`);

  const switchToAcademy = () => {
    if (!operatedAcademy) { navigate('/academy/register'); return; }
    setSwitching('academy');
    setTimeout(() => {
      setMode('academy');
      // 학원모드 진입 = 학부모가 보는 공개 페이지(둘러보기)부터
      navigate(`/app/academy/${operatedAcademy.id}`);
    }, 1200);
  };
  const switchToPersonal = () => {
    setSwitching('personal');
    setTimeout(() => { setMode('personal'); navigate('/app/home'); }, 1200);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.uid) return;
    setUploading(true);
    setProgress(0);
    const compressed = await compressImage(file, 400, 0.8);
    const task = uploadBytesResumable(ref(storage, `avatars/${userData.uid}`), compressed);
    task.on(
      'state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error('프로필 사진 변경 실패:', err);
        alert('사진 변경에 실패했어요.');
        setUploading(false);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          // 캐시 깨기 위한 쿼리스트링 추가 (같은 경로 덮어쓰기여서 브라우저가 옛 이미지 캐시)
          const cacheBustedUrl = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
          await updateDoc(doc(db, 'users', userData.uid), { photoURL: cacheBustedUrl });
          await refreshUser();
        } catch (err) {
          console.error('프로필 사진 변경 실패:', err);
          alert('사진 변경에 실패했어요.');
        } finally {
          setUploading(false);
        }
      },
    );
    e.target.value = '';
  };

  const openNameEdit = () => {
    setNameInput(userData?.name || '');
    setNameErr('');
    setNameModal(true);
  };

  const saveName = async () => {
    const v = nameInput.trim();
    if (!v) { setNameErr('대화명을 입력해 주세요.'); return; }
    if (v.length > 16) { setNameErr('16자 이하로 입력해 주세요.'); return; }
    if (v === userData?.name) { setNameModal(false); return; }
    setNameSaving(true);
    setNameErr('');
    try {
      const dup = await getDocs(query(collection(db, 'users'), where('name', '==', v), limit(1)));
      if (!dup.empty && dup.docs[0].id !== userData.uid) {
        setNameErr('이미 사용 중인 대화명이에요.');
        return;
      }
      await updateDoc(doc(db, 'users', userData.uid), { name: v });
      await refreshUser();
      setNameModal(false);
    } catch (e) {
      console.error('대화명 변경 실패:', e);
      setNameErr('변경에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setNameSaving(false);
    }
  };

  const handleLogout = async () => {
    if (!(await confirm({ title: '로그아웃', message: '로그아웃할까요?' }))) return;
    await logout();
    navigate('/app/login', { replace: true });
  };

  const handleWithdraw = async () => {
    const ok = await confirm({
      title: '서비스 탈퇴',
      message: '계정과 모든 정보가 삭제되고\n되돌릴 수 없어요. 정말 탈퇴할까요?',
      confirmText: '탈퇴',
      danger: true,
    });
    if (!ok) return;
    showLoading({
      title: '탈퇴 처리 중',
      message: '회원님의 정보를 모두\n삭제하고 있어요',
    });
    try {
      if (userData?.uid) {
        await deleteObject(ref(storage, `avatars/${userData.uid}`)).catch(() => {});
      }
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(APP_CONFIG.deleteAccountUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'failed');
      await logout();
      hideLoading();
      await alert('탈퇴가 완료됐어요.');
      navigate('/app/login', { replace: true });
    } catch (e) {
      console.error('탈퇴 실패:', e);
      hideLoading();
      alert('탈퇴 처리에 실패했어요. 다시 시도해 주세요.');
    }
  };

  return (
    <Container>
      {switching && <ModeSwitchSplash to={switching} />}
      <Profile>
        {academyMode ? (
          <AvatarWrap>
            {operatedAcademy?.photoUrl
              ? <AvatarImg src={operatedAcademy.photoUrl} alt="학원" />
              : <Avatar>{(operatedAcademy?.name || '?').slice(0, 1)}</Avatar>}
          </AvatarWrap>
        ) : (
          <AvatarWrap as="label" htmlFor="my-avatar-upload">
            {userData?.photoURL
              ? <AvatarImg src={userData.photoURL} alt="프로필" />
              : <Avatar>{(userData?.name || '?').slice(0, 1)}</Avatar>}
            <CamBadge><IoCameraOutline size={12} /></CamBadge>
            {uploading && <Uploading><Spinner /><Pct>{progress}%</Pct></Uploading>}
            <HiddenInput
              id="my-avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              disabled={uploading}
            />
          </AvatarWrap>
        )}
        <ProfileText>
          <NameRow>
            <Name>
              {academyMode
                ? (operatedAcademy?.name || '학원')
                : (userData?.name || '사용자')}
            </Name>
            {academyMode ? (
              <NameEditBtn type="button" onClick={() => navigate('/app/biz')}>변경</NameEditBtn>
            ) : (
              <NameEditBtn type="button" onClick={openNameEdit}>변경</NameEditBtn>
            )}
          </NameRow>
          <Role>{isOwner ? '원장' : '학부모'}</Role>
        </ProfileText>
      </Profile>

      <Group>
        {!isDemo && !academyMode && (userData?.children || []).length === 0 && (
          <Tooltip>
            <TipText>자녀를 등록하면 학원 수강 신청을 할 수 있어요</TipText>
            <TipTail />
          </Tooltip>
        )}
        {!academyMode && (
          <>
            <Item onClick={openChildSheet}>
              <Label>자녀 관리</Label>
              <ChildrenSummary>
                <ChildrenCount>
                  {(userData?.children || []).length > 0
                    ? `${(userData.children || []).length}명`
                    : '등록'}
                </ChildrenCount>
                <IoChevronForward size={18} />
              </ChildrenSummary>
            </Item>
            {(userData?.children || []).map((c) => (
              <ChildRow key={c.id} onClick={openChildSheet}>
                <ChildAvatar photoURL={c.photoURL} name={c.name} size={48} />
                <ChildRowInfo>
                  <ChildRowName>{c.name}</ChildRowName>
                  {c.birthYear ? (
                    <ChildRowMeta>{c.birthYear}년생 · {koreanAge(c.birthYear)}살</ChildRowMeta>
                  ) : (
                    <ChildRowMeta $hint>출생연도 미입력</ChildRowMeta>
                  )}
                </ChildRowInfo>
              </ChildRow>
            ))}
          </>
        )}
        <Item onClick={() => soon(billingLabel)}>
          <Label>{billingLabel}</Label>
          <IoChevronForward size={18} />
        </Item>
        <Item onClick={() => soon('알림 설정')}>
          <Label>알림 설정</Label>
          <IoChevronForward size={18} />
        </Item>
        <Item onClick={() => soon('문의 / 상담')}>
          <Label>문의 / 상담</Label>
          <IoChevronForward size={18} />
        </Item>
      </Group>

      <Group>
        <Item onClick={() => navigate(`/app/terms/${academyMode ? 'academy' : 'parent'}`)}>
          <Label>이용약관</Label>
          <IoChevronForward size={18} />
        </Item>
        <Item onClick={() => navigate(`/app/privacy/${academyMode ? 'academy' : 'parent'}`)}>
          <Label>개인정보 처리방침</Label>
          <IoChevronForward size={18} />
        </Item>
        <Item as="div">
          <Label>버전 정보</Label>
          <Version>{APP_VERSION}</Version>
        </Item>
      </Group>

      <Group>
        {!isDemo && (
          <Item onClick={handleLogout}>
            <Label>로그아웃</Label>
            <IoChevronForward size={18} />
          </Item>
        )}
        {!isDemo && (
          <Item onClick={handleWithdraw}>
            <Label>서비스 탈퇴하기</Label>
            <IoChevronForward size={18} />
          </Item>
        )}
      </Group>

      {nameModal && (
        <Overlay onClick={() => setNameModal(false)}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetTitle>대화명 변경</SheetTitle>
            <SheetSub>다른 사용자에게 보여지는 이름이에요.</SheetSub>
            <NameInput
              autoFocus
              value={nameInput}
              maxLength={16}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
            />
            {nameErr && <ErrText>{nameErr}</ErrText>}
            <SheetActions>
              <CancelBtn type="button" onClick={() => setNameModal(false)}>취소</CancelBtn>
              <SaveBtn type="button" disabled={nameSaving} onClick={saveName}>
                {nameSaving ? '저장 중...' : '저장'}
              </SaveBtn>
            </SheetActions>
          </Sheet>
        </Overlay>
      )}

      <ChildrenSheet
        open={childSheetOpen}
        onClose={() => setChildSheetOpen(false)}
      />
    </Container>
  );
};

export default MyPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  min-height: 100%;
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 20px;
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  cursor: pointer;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 23px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImg = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
`;

const CamBadge = styled.span`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid #fff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Uploading = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: my-avatar-spin 0.7s linear infinite;
  @keyframes my-avatar-spin {
    to { transform: rotate(360deg); }
  }
`;

const Pct = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: #fff;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProfileText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Name = styled.p`
  font-size: 19px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Role = styled.span`
  align-self: flex-start;
  padding: 2px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const Tooltip = styled.div`
  position: relative;
  margin: 16px 20px 5px;
  padding: 11px 14px;
  border-radius: 12px;
  background: #22B07D;
`;

const TipText = styled.p`
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
`;

const TipTail = styled.span`
  position: absolute;
  left: 26px;
  bottom: -5px;
  width: 12px;
  height: 12px;
  background: #22B07D;
  transform: rotate(45deg);
`;

const Group = styled.div`
  border-top: 8px solid #F4F5F7;
`;

const Item = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  &:last-child { border-bottom: none; }
  svg { color: ${({ theme }) => theme.colors.text.hint}; }
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Version = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameEditBtn = styled.button`
  padding: 4px 10px;
  border-radius: 8px;
  background: #F4F5F7;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 18px 28px;
`;

const SheetTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SheetSub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 6px 0 16px;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 15px 16px;
  border: 0;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 16px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const ErrText = styled.p`
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.red};
`;

const SheetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;

const ChildrenSummary = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const ChildRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 20px 12px 28px;
  background: #F9FAFC;
  border-bottom: 1px solid #F0F1F4;
  text-align: left;
  &:last-of-type { border-bottom: none; }
`;

const ChildRowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChildRowName = styled.p`
  font-size: 14.5px;
  font-weight: 700;
  color: #1F2937;
`;

const ChildRowMeta = styled.p`
  margin-top: 2px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ $hint }) => ($hint ? '#9CA3AF' : '#6B7280')};
`;

const ChildrenCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
